import React, { useState, useEffect } from "react";
import { Box, Grid, Button, Typography, MenuItem, Select, InputLabel, FormControl, IconButton, Paper, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { CloudUpload, Download } from "@mui/icons-material";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { uploadPayroll, getPayrollByMonth, updatePayrollStatus, getPayrollFile } from "../../services/payroll.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import dayjs from "dayjs";

const PayrollManagement = () => {
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [month, setMonth] = useState(selectedDate.month() + 1);
    const [year, setYear] = useState(selectedDate.year());
    const [tableTotalCount, setTableTotalCount] = useState(0);

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [revertDialogOpen, setRevertDialogOpen] = useState(false);
    const isAdmin = true; // Hardcoded as per user preference for now

    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [uploadMonth, setUploadMonth] = useState(month);
    const [uploadYear, setUploadYear] = useState(year);
    const [selectedFile, setSelectedFile] = useState(null);
    const [hasOriginalFile, setHasOriginalFile] = useState(false);

    const months = [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
    ];

    // Generate years dynamically (last 5 years + next 1 year)
    const years = Array.from({ length: 6 }, (_, i) => dayjs().year() - 4 + i);

    useEffect(() => {
        fetchData();
    }, [month, year]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getPayrollByMonth(month, year);
            if (res.data && res.data.length > 0) {
                setData(res.data);
                setTableTotalCount(res.data.length);
            } else {
                setData([]);
                setTableTotalCount(0);
            }

            // Check if original file exists for this month/year
            try {
                const fileRes = await getPayrollFile(month, year);
                setHasOriginalFile(!!fileRes.data);
            } catch (error) {
                setHasOriginalFile(false);
            }
        } catch (error) {
            console.error(error);
            ToastError("Failed to fetch payroll data");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenUploadDialog = () => {
        setUploadMonth(month);
        setUploadYear(year);
        setSelectedFile(null);
        setOpenUploadDialog(true);
    };

    const handleCloseUploadDialog = () => {
        setOpenUploadDialog(false);
        setSelectedFile(null);
    };

    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
        }
    };

    const handleUploadSubmit = async () => {
        if (!selectedFile) {
            ToastError("Please select a file to upload");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("month", uploadMonth);
        formData.append("year", uploadYear);

        setActionLoading(true);
        try {
            await uploadPayroll(formData);
            ToastSuccess("Payroll uploaded successfully");
            handleCloseUploadDialog();
            fetchData(); // Refresh data and check for original file
        } catch (error) {
            console.error(error);
            ToastError(error?.data?.message || "Failed to upload payroll");
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async (row) => {
        if (row.status === "Waiting") {
            setSelectedItem(row);
            setConfirmDialogOpen(true);
            return;
        }

        setActionLoading(true);
        try {
            await updatePayrollStatus(row._id, "Approved");
            ToastSuccess("Payroll Approved Successfully");
            fetchData();
        } catch (error) {
            console.error("Error approving payroll", error);
            ToastError("Failed to approve payroll");
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmSuccess = async () => {
        if (!selectedItem) return;

        setActionLoading(true);
        try {
            await updatePayrollStatus(selectedItem._id, "Success");
            ToastSuccess("Payroll Status Updated to Success");
            fetchData();
            handleCloseConfirmDialog();
        } catch (error) {
            console.error("Error updating payroll status", error);
            ToastError("Failed to update status");
        } finally {
            setActionLoading(false);
        }
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
        setSelectedItem(null);
    };

    const handleRevertToWaiting = (row) => {
        setSelectedItem(row);
        setRevertDialogOpen(true);
    };

    const handleConfirmRevert = async () => {
        if (!selectedItem) return;

        setActionLoading(true);
        try {
            await updatePayrollStatus(selectedItem._id, "Waiting");
            ToastSuccess("Payroll Status Reverted to Waiting");
            fetchData();
            handleCloseRevertDialog();
        } catch (error) {
            console.error("Error reverting payroll status", error);
            ToastError("Failed to revert status");
        } finally {
            setActionLoading(false);
        }
    };

    const handleCloseRevertDialog = () => {
        setRevertDialogOpen(false);
        setSelectedItem(null);
    };

    const handleDownloadOriginalExcel = async () => {
        try {
            setActionLoading(true);
            const res = await getPayrollFile(month, year);

            if (res.data && res.data.s3Url) {
                // Open S3 URL in new tab to trigger download
                window.open(res.data.s3Url, '_blank');
                ToastSuccess("Downloading original Excel file...");
            } else {
                ToastError("No uploaded file found for this month/year");
            }
        } catch (error) {
            console.error("Error downloading file:", error);
            ToastError("Failed to download original Excel file");
        } finally {
            setActionLoading(false);
        }
    };

    const columns = [
        { id: "userName", label: "Employee Name", minWidth: 150 },
        { id: "emp_id", label: "Emp ID", minWidth: 100 },
        { id: "uan", label: "UAN", minWidth: 120 },
        { id: "esicNumber", label: "ESIC No", minWidth: 120 }, // Updated key
        { id: "basicSalary", label: "Basic", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "hra", label: "HRA", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "conveyance", label: "Conveyance", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "incomeTax", label: "Income Tax", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "pf", label: "PF", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "lopAmount", label: "LOP", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "esicAmount", label: "ESIC Ded", minWidth: 100, format: (value) => value.toFixed(2) }, // Updated key
        { id: "professionalTax", label: "Prof Tax", minWidth: 100, format: (value) => value.toFixed(2) },
        { id: "netSalary", label: "Net Salary", minWidth: 120, format: (value) => value.toFixed(2) },
        { id: "status", label: "Status", minWidth: 100 },
    ];

    const tableConfig = {
        isMore: true,
        isAction: true,
        isView: false,
        isEdit: false,
        isToggle: false,
        isDelete: false,
        isAdmin: true,
        isApprove: true,
        isRevert: true,
    };

    return (
        <div className="mainPageRenderingContainer">
            <Paper elevation={0} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, backgroundColor: 'transparent' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Month</InputLabel>
                        <Select
                            value={month}
                            label="Month"
                            onChange={(e) => setMonth(e.target.value)}
                        >
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Year</InputLabel>
                        <Select
                            value={year}
                            label="Year"
                            onChange={(e) => setYear(e.target.value)}
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {hasOriginalFile && (
                        <Button
                            startIcon={<Download />}
                            onClick={handleDownloadOriginalExcel}
                            disabled={loading || actionLoading}
                            className="Add-Button"
                            sx={{
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                    borderColor: '#115293',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                },
                                minWidth: '200px'
                            }}
                        >
                            Download Original
                        </Button>
                    )}
                    <Button
                        startIcon={<CloudUpload />}
                        onClick={handleOpenUploadDialog}
                        disabled={loading || actionLoading}
                        className="Add-Button"
                    >
                        Upload Excel
                    </Button>
                </Box>
            </Paper>

            <MasterCommonTable
                tableConfig={tableConfig}
                data={data}
                columns={columns}
                tableTotalCount={tableTotalCount}
                loading={loading}
                handleApprove={handleApprove}
                handleRevertToWaiting={handleRevertToWaiting}
            />

            {/* Upload Dialog */}
            <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Payroll for Month</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Month</InputLabel>
                                <Select
                                    value={uploadMonth}
                                    label="Month"
                                    onChange={(e) => setUploadMonth(e.target.value)}
                                >
                                    {months.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={uploadYear}
                                    label="Year"
                                    onChange={(e) => setUploadYear(e.target.value)}
                                >
                                    {years.map((y) => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ border: '2px dashed #999', borderRadius: 2, p: 3, textAlign: 'center' }}>
                            <input
                                accept=".xlsx, .xls"
                                style={{ display: 'none' }}
                                id="dialog-upload-file"
                                type="file"
                                onChange={handleFileSelect}
                            />
                            <label htmlFor="dialog-upload-file">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<CloudUpload />}
                                    sx={{ mb: 1 }}
                                >
                                    Select Excel File
                                </Button>
                            </label>
                            {selectedFile ? (
                                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                    Selected: {selectedFile.name}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    No file selected
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseUploadDialog} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleUploadSubmit}
                        variant="contained"
                        disabled={!selectedFile || actionLoading}
                        className="Add-Button"
                        sx={{ maxWidth: '150px' }}
                    >
                        {actionLoading ? "Uploading..." : "Upload"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Status Change</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 2 }}>
                        Are you sure you want to change the status from <b>Waiting</b> to <b>Success</b>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseConfirmDialog} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleConfirmSuccess}
                        variant="contained"
                        disabled={actionLoading}
                        className="Add-Button"
                        sx={{ maxWidth: '150px' }}
                    >
                        {actionLoading ? "Updating..." : "Success"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revert Confirmation Dialog */}
            <Dialog open={revertDialogOpen} onClose={handleCloseRevertDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Status Revert</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 2 }}>
                        Are you sure you want to revert the status from <b>Success</b> back to <b>Waiting</b>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseRevertDialog} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleConfirmRevert}
                        variant="contained"
                        color="warning"
                        disabled={actionLoading}
                        className="Add-Button"
                        sx={{ maxWidth: '150px' }}
                    >
                        {actionLoading ? "Reverting..." : "Waiting"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PayrollManagement;
